import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Divider, Link, Table, TableBody, TableContainer } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAppDispatcher } from 'src/redux/store';
import {
  createPackagingStockCount,
  TCreatePackagingStockCount,
  updatePackagingStockCount,
} from 'src/redux/slices/packaging-stock-count';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useSnackbar as useAppSnackbar } from 'src/components/snackbar';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import { IPackagingStockCountDetailedItem } from 'src/types/packaging-stock-count';

import { paths } from '../../routes/paths';
import PackagingStockCountNewItemRow from './packaging-stock-count-new-item-row';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemNo', label: '#', align: 'left', primary: true },
  { id: 'packagingItem', label: 'Packaging Item', align: 'left', primary: true },
  { id: 'addedQuantity', label: 'Quantity Counted', align: 'left', primary: true },
  { id: '', primary: false },
];

type Props = {
  currentStockCount?: IPackagingStockCountDetailedItem;
};

export default function PackagingStockCountNewEditForm({ currentStockCount }: Props) {
  const dispatch = useAppDispatcher();
  const { enqueueSnackbar } = useAppSnackbar();
  const navigate = useNavigate();

  const NewPackagingStockCountSchema = Yup.object().shape({
    countDate: Yup.date().typeError('Invalid date').required('Count date is required'),
    remarks: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object().shape({
          packagingItem: Yup.string().required('Packaging item is required'),
          addedQuantity: Yup.number()
            .typeError('')
            .moreThan(0, 'Quantity must be greater than zero')
            .required('Quantity is required'),
        })
      )
      .min(1, 'At least one item is required')
      .required('Items are required')
      .test(
        'unique-packaging-item',
        'Each packaging item can only appear once — merge these lines instead',
        (items) => {
          const keys = (items ?? [])
            .filter((item) => item?.packagingItem)
            .map((item) => item.packagingItem);
          return new Set(keys).size === keys.length;
        }
      ),
  });

  const defaultItem = { packagingItem: '', addedQuantity: 1 };

  const defaultValues = useMemo(
    () => ({
      countDate: currentStockCount?.countDate ? new Date(currentStockCount.countDate) : new Date(),
      remarks: currentStockCount?.remarks ?? null,
      items: currentStockCount?.items.map((item) => ({
        packagingItem: item.packagingItem._id,
        addedQuantity: item.addedQuantity,
      })) ?? [defaultItem],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStockCount]
  );

  const methods = useForm({
    resolver: yupResolver(NewPackagingStockCountSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload: TCreatePackagingStockCount = {
        ...data,
        remarks: data.remarks ?? null,
      };

      if (currentStockCount) {
        await dispatch(
          updatePackagingStockCount({
            stockCountId: currentStockCount._id,
            stockCountTransaction: payload,
          })
        ).unwrap();

        enqueueSnackbar('Stock count updated');
      } else {
        await dispatch(
          createPackagingStockCount({ stockCountTransaction: payload })
        ).unwrap();

        enqueueSnackbar('Stock count created');
      }

      navigate(paths.dashboard.packagingStockCount.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Stock Count Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="countDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      disableFuture
                      label="Count Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField name="remarks" label="Remarks" multiline rows={2} />
              </Grid>

              <Divider
                sx={{
                  width: 1,
                  my: 2,
                  borderStyle: 'dashed',
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography variant="subtitle2">Line Items</Typography>

                {typeof errors.items?.message === 'string' && (
                  <Typography variant="caption" color="error.main" component="p" mt={0.5}>
                    {errors.items.message}
                  </Typography>
                )}
              </Grid>

              <Grid xs={12}>
                <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                  <Scrollbar>
                    <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
                      <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                      <TableBody>
                        {fields.map((item, index) => (
                          <PackagingStockCountNewItemRow
                            key={item.id}
                            index={index}
                            remove={remove}
                            isLast={fields.length === 1 && index === 0}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </TableContainer>

                <Stack mt={2}>
                  <Button variant="soft" color="primary" onClick={() => append(defaultItem)}>
                    Add
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="space-between" gap={2} mt={4}>
              <Link component={RouterLink} href={paths.dashboard.packagingStockCount.root}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {!currentStockCount ? 'Create Stock Count' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
